/**
 * Painel de Análise de Sentimento
 * Exibe sentimentos detectados por segmento com visualizações
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt'
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral'
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import RefreshIcon from '@mui/icons-material/Refresh'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { SentimentAnalysis, SentimentLabel, SegmentSentiment } from '../../types/transcription'
import { getSentimentAnalysis } from '../../services/api'
import { contextualErrors, FriendlyError } from '../../services/errorHandler'
import AnalysisNotAvailableCard from './AnalysisNotAvailableCard'

type Props = {
  fileId: string
  onNavigateToSegment?: (segmentIndex: number) => void
}

const SENTIMENT_CONFIG = {
  positive: {
    label: 'Positivo',
    icon: SentimentSatisfiedAltIcon,
    color: '#4caf50',
    bgColor: '#e8f5e9'
  },
  neutral: {
    label: 'Neutro',
    icon: SentimentNeutralIcon,
    color: '#9e9e9e',
    bgColor: '#fafafa'
  },
  negative: {
    label: 'Negativo',
    icon: SentimentDissatisfiedIcon,
    color: '#f44336',
    bgColor: '#ffebee'
  }
}

function SentimentIcon({ sentiment, size = 24 }: { sentiment: SentimentLabel; size?: number }) {
  const config = SENTIMENT_CONFIG[sentiment]
  const Icon = config.icon
  return <Icon sx={{ color: config.color, fontSize: size }} />
}

function SentimentBar({ value, label, color }: { value: number; label: string; color: string }) {
  const safeValue = value ?? 0
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color }}>
          {safeValue.toFixed(1)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={safeValue}
        sx={{
          height: 10,
          borderRadius: 5,
          bgcolor: '#f0f0f0',
          '& .MuiLinearProgress-bar': {
            bgcolor: color,
            borderRadius: 5
          }
        }}
      />
    </Box>
  )
}

function ScoreGauge({ score }: { score: number }) {
  const safeScore = score ?? 0
  // score de -1 a 1, convertemos para 0-100
  const percentage = ((safeScore + 1) / 2) * 100
  const color = safeScore > 0.2 ? '#4caf50' : safeScore < -0.2 ? '#f44336' : '#ff9800'
  
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={120}
        thickness={6}
        sx={{ color: '#f0f0f0' }}
      />
      <CircularProgress
        variant="determinate"
        value={percentage}
        size={120}
        thickness={6}
        sx={{
          color,
          position: 'absolute',
          left: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color }}>
          {safeScore > 0 ? '+' : ''}{safeScore.toFixed(2)}
        </Typography>
        <Typography variant="caption" sx={{ color: '#666' }}>
          Score Médio
        </Typography>
      </Box>
    </Box>
  )
}

function SegmentCard({ segment, onNavigateToSegment }: { segment: SegmentSentiment; onNavigateToSegment?: (segmentIndex: number) => void }) {
  const config = SENTIMENT_CONFIG[segment.sentiment] || SENTIMENT_CONFIG.neutral
  const safeKeywords = segment.keywords ?? []
  const safeScore = segment.score ?? 0
  const safeConfidence = segment.confidence ?? 0
  
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 1.5,
        border: '1px solid #e0e0e0',
        borderLeft: `4px solid ${config.color}`,
        bgcolor: config.bgColor,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: config.color,
          boxShadow: 1
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <SentimentIcon sentiment={segment.sentiment} size={28} />
        
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip
              label={segment.speaker}
              size="small"
              sx={{ fontWeight: 500, fontSize: '0.7rem' }}
            />
            <Typography variant="caption" sx={{ color: '#666' }}>
              {formatTime(segment.startTime)}
            </Typography>
            <Chip
              label={`${(safeConfidence * 100).toFixed(0)}% confiança`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem' }}
            />
          </Box>
          
          <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.5, mb: 1 }}>
            "{segment.text}"
          </Typography>
          
          {safeKeywords.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: '#666', mr: 0.5 }}>
                Palavras-chave:
              </Typography>
              {safeKeywords.map((kw, i) => (
                <Chip
                  key={i}
                  label={kw}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              ))}
            </Box>
          )}
        </Box>
        
        <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: config.color
            }}
          >
            {safeScore > 0 ? '+' : ''}{safeScore.toFixed(2)}
          </Typography>
          {onNavigateToSegment && (
            <Tooltip title="Ir para o segmento na transcrição">
              <IconButton
                size="small"
                onClick={() => onNavigateToSegment(segment.segmentIndex)}
                sx={{ 
                  color: config.color,
                  '&:hover': { bgcolor: config.bgColor }
                }}
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
  )
}

/**
 * Formata o tempo para exibição.
 * Aceita tanto segundos (número ou string numérica) quanto formato HH:MM:SS.
 */
function formatTime(time: string): string {
  if (!time) return '0:00'
  
  // Se já está no formato HH:MM:SS ou MM:SS, retorna diretamente (ou simplificado)
  if (time.includes(':')) {
    const parts = time.split(':')
    if (parts.length === 3) {
      // HH:MM:SS - remover horas se for 00
      const hours = parseInt(parts[0], 10)
      const mins = parseInt(parts[1], 10)
      const secs = parseInt(parts[2], 10)
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    // MM:SS - retorna como está
    return time
  }
  
  // Se for número de segundos
  const seconds = parseFloat(time)
  if (isNaN(seconds)) return '0:00'
  
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function SentimentAnalysisPanel({ fileId, onNavigateToSegment }: Props) {
  const [analysis, setAnalysis] = useState<SentimentAnalysis | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [filter, setFilter] = useState<SentimentLabel | 'all'>('all')

  const loadAnalysis = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getSentimentAnalysis(fileId)
      setAnalysis(data)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
      console.error('Erro ao carregar análise de sentimento:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAnalysis()
  }, [fileId])

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2, color: '#666' }}>
            Analisando sentimentos...
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Box sx={{ fontSize: 48, mb: 2 }}>⚠️</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {error.title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            {error.message}
          </Typography>
          {error.canRetry && (
            <Button variant="outlined" onClick={loadAnalysis} startIcon={<RefreshIcon />}>
              Tentar novamente
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  if (!analysis) {
    return (
      <AnalysisNotAvailableCard 
        title="Análise de Sentimento" 
        onRetry={loadAnalysis} 
      />
    )
  }

  // Valores seguros com fallbacks
  const overall = analysis.overall ?? { positive: 0, neutral: 0, negative: 0, averageScore: 0 }
  const segments = analysis.segments ?? []

  const filteredSegments = filter === 'all'
    ? segments
    : segments.filter(s => s.sentiment === filter)

  const trend = overall.averageScore > 0.1 
    ? 'up' 
    : overall.averageScore < -0.1 
    ? 'down' 
    : 'flat'

  return (
    <Box>
      {/* Resumo Geral */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              😊 Análise de Sentimento
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadAnalysis}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Atualizar
            </Button>
          </Box>

          <Grid container spacing={4} alignItems="center">
            {/* Gauge Central */}
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <ScoreGauge score={overall.averageScore} />
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                {trend === 'up' && <TrendingUpIcon sx={{ color: '#4caf50' }} />}
                {trend === 'down' && <TrendingDownIcon sx={{ color: '#f44336' }} />}
                {trend === 'flat' && <TrendingFlatIcon sx={{ color: '#ff9800' }} />}
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {trend === 'up' && 'Tendência positiva'}
                  {trend === 'down' && 'Tendência negativa'}
                  {trend === 'flat' && 'Tendência neutra'}
                </Typography>
              </Box>
            </Grid>

            {/* Barras de Distribuição */}
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Distribuição de Sentimentos
              </Typography>
              <SentimentBar
                value={overall.positive}
                label="😊 Positivo"
                color={SENTIMENT_CONFIG.positive.color}
              />
              <SentimentBar
                value={overall.neutral}
                label="😐 Neutro"
                color={SENTIMENT_CONFIG.neutral.color}
              />
              <SentimentBar
                value={overall.negative}
                label="😔 Negativo"
                color={SENTIMENT_CONFIG.negative.color}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1}>
          <Chip
            label="Todos"
            onClick={() => setFilter('all')}
            color={filter === 'all' ? 'primary' : 'default'}
            variant={filter === 'all' ? 'filled' : 'outlined'}
          />
          <Chip
            icon={<SentimentSatisfiedAltIcon />}
            label="Positivos"
            onClick={() => setFilter('positive')}
            sx={{
              bgcolor: filter === 'positive' ? SENTIMENT_CONFIG.positive.bgColor : undefined,
              borderColor: filter === 'positive' ? SENTIMENT_CONFIG.positive.color : undefined
            }}
            variant={filter === 'positive' ? 'filled' : 'outlined'}
          />
          <Chip
            icon={<SentimentNeutralIcon />}
            label="Neutros"
            onClick={() => setFilter('neutral')}
            sx={{
              bgcolor: filter === 'neutral' ? SENTIMENT_CONFIG.neutral.bgColor : undefined,
              borderColor: filter === 'neutral' ? SENTIMENT_CONFIG.neutral.color : undefined
            }}
            variant={filter === 'neutral' ? 'filled' : 'outlined'}
          />
          <Chip
            icon={<SentimentDissatisfiedIcon />}
            label="Negativos"
            onClick={() => setFilter('negative')}
            sx={{
              bgcolor: filter === 'negative' ? SENTIMENT_CONFIG.negative.bgColor : undefined,
              borderColor: filter === 'negative' ? SENTIMENT_CONFIG.negative.color : undefined
            }}
            variant={filter === 'negative' ? 'filled' : 'outlined'}
          />
        </Stack>
      </Box>

      {/* Lista de Segmentos */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Segmentos Analisados ({filteredSegments.length})
          </Typography>

          {filteredSegments.map((segment, index) => (
            <SegmentCard key={index} segment={segment} onNavigateToSegment={onNavigateToSegment} />
          ))}

          {filteredSegments.length === 0 && (
            <Typography sx={{ textAlign: 'center', color: '#666', py: 4 }}>
              Nenhum segmento encontrado com este filtro
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Legenda */}
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: '#666' }}>
          💡 <strong>Como interpretar:</strong> O score varia de -1 (muito negativo) a +1 (muito positivo).
          Valores próximos de 0 indicam sentimento neutro. A análise considera o tom emocional
          das falas, identificando palavras-chave que influenciam o sentimento detectado.
          {onNavigateToSegment && ' Use o botão ➔ para ir diretamente ao segmento na aba Transcrição.'}
        </Typography>
      </Box>
    </Box>
  )
}
